import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../app/theme';
import { NodeType } from '../../app/types';
import closeImg from '../../assets/images/svg/ic_editer_close.svg';
import {
  highlightPath,
  setHighlightPathNodes,
  setIsPreview,
  setSelectedNode,
} from '../../features/mainSlice';
import PreviewConditionNode from './PreviewConditionNode';
import PreviewCouponNode from './PreviewCouponNode';
import PreviewCustomerConditionNode from './PreviewCustomerConditionNode';
import PreviewGAConditionNode from './PreviewGAConditionNode';
import PreviewInboxNode from './PreviewInboxNode';
import PreviewMANode from './PreviewMANode';
import PreviewMessage from './PreviewMessage';
import PreviewScheduleNode from './PreviewScheduleNode';
import PreviewTagNode from './PreviewTagNode';
import PreviewTrigger from './PreviewTrigger';
import PreviewWaitNode from './PreviewWaitNode';

const useStyles = makeStyles(
  {
    root: {
      position: 'fixed',
      top: 114,
      right: '0',
      zIndex: 999,
      width: 360,
      background: color.$7,
      boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.08)',
    },
    title: {
      fontSize: '17px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '56px',
      padding: '0 16px',
      '& > img': {
        cursor: 'pointer',
      },
    },
    subtitle: {
      display: 'flex',
      alignItems: 'center',
      height: '48px',
      background: color.$3,
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.38',
      letterSpacing: 'normal',
      color: color.$7,
      padding: '0 16px',
    },
    content: {
      height: 'calc(100vh - 220px)',
      padding: '24px 16px 36px',
      overflowY: 'auto',
    },
  },
  {
    name: 'Preview',
  },
);

const Preview = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isPreview, data, selectedNode, highlightPathNodes } = useSelector(
    store => store.ma,
  );
  const { icon } = useSelector(store => store.organization);

  const previewNodeComponents = useMemo(
    () =>
      highlightPathNodes.map(node => {
        const { id, type } = node || {};
        switch (type) {
          case NodeType.START:
          case NodeType.END:
          default:
            return null;
          case NodeType.TRIGGER:
            return <PreviewTrigger key={`preview-${id}`} node={node} />;
          case NodeType.TAGGING:
            return <PreviewTagNode key={`preview-${id}`} node={node} />;
          case NodeType.MESSAGE:
            return (
              <PreviewMessage
                key={`preview-${id}`}
                node={node}
                orgIconURL={icon}
              />
            );
          case NodeType.CONDITION:
            return <PreviewConditionNode key={`preview-${id}`} node={node} />;
          case NodeType.GACONDITION:
            return <PreviewGAConditionNode key={`preview-${id}`} node={node} />;
          case NodeType.MA:
            return <PreviewMANode key={`preview-${id}`} node={node} />;
          case NodeType.INBOX:
            return <PreviewInboxNode key={`preview-${id}`} node={node} />;
          case NodeType.COUPON:
            return <PreviewCouponNode key={`preview-${id}`} node={node} />;
          case NodeType.CUSTOMERCONDITION:
            return (
              <PreviewCustomerConditionNode key={`preview-${id}`} node={node} />
            );
          case NodeType.SCHEDULE:
            return <PreviewScheduleNode key={`preview-${id}`} node={node} />;
          case NodeType.WAIT:
            return <PreviewWaitNode key={`preview-${id}`} node={node} />;
        }
      }),
    [highlightPathNodes],
  );

  const onClosePreview = () => {
    dispatch(setSelectedNode(null));
    dispatch(setIsPreview(false));
  };

  useEffect(() => {
    if (isPreview && selectedNode) {
      dispatch(highlightPath());
    } else {
      dispatch(setHighlightPathNodes([]));
    }
  }, [isPreview, selectedNode, data]);

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        {t('preview-title')}
        <img src={closeImg} onClick={onClosePreview} alt="close" />
      </div>
      <div className={classes.subtitle}>{t('preview-subtitle')}</div>
      <div className={classes.content}>{previewNodeComponents}</div>
    </div>
  );
};

export default Preview;
